// frontend/src/components/common/DataTable.js
import React, { useState, useMemo } from 'react';
import {
  ChevronUp, 
  ChevronDown, 
  Search, 
  Filter,
  Download,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

// A column with `stickyRight: true` stays pinned to the table wrapper's right
// edge while the other columns scroll sideways under it. Every cell is
// whitespace-nowrap, so a wide table overflows its overflow-x-auto wrapper, and
// a row-actions column at the end was cut off on laptop and tablet widths.
// Sticky cells need an opaque background that follows the row's hover, and a
// shadow rather than a border for the divider: collapsed table borders do not
// travel with a sticky cell.
const STICKY_DIVIDER = 'shadow-[-1px_0_0_theme(colors.border.default)]';
const STICKY_RIGHT_HEAD = `sticky right-0 z-10 bg-surface-sunken ${STICKY_DIVIDER}`;
const STICKY_RIGHT_CELL = `sticky right-0 bg-surface-card group-hover:bg-surface-sunken ${STICKY_DIVIDER}`;

export const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  onRowClick = null,
  onRowSelect = null,
  selectable = false,
  sortable = true,
  filterable = true,
  pagination = true,
  pageSize = 10,
  className = '',
  emptyMessage = 'No data available',
  actions = null,
  onExport = null,
  density = 'comfortable',
  renderCard = null
}) => {
  const cellPadding = density === 'compact' ? 'py-1.5' : 'py-3';
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Filtering
  const filteredData = useMemo(() => {
    if (!filterText) return data;
    
    return data.filter((item) =>
      columns.some((column) => {
        const value = getNestedValue(item, column.accessor);
        return String(value).toLowerCase().includes(filterText.toLowerCase());
      })
    );
  }, [data, filterText, columns]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Helper function to get nested object values
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;
    
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle row selection
  const handleRowSelect = (rowIndex, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(rowIndex);
    } else {
      newSelected.delete(rowIndex);
    }
    setSelectedRows(newSelected);
    onRowSelect?.(Array.from(newSelected));
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIndexes = paginatedData.map((_, index) => index);
      setSelectedRows(new Set(allIndexes));
      onRowSelect?.(allIndexes);
    } else {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    }
  };

  // Render cell content
  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item, getNestedValue(item, column.accessor));
    }
    
    const value = getNestedValue(item, column.accessor);
    
    if (column.type === 'currency') {
      return `$${Number(value).toFixed(2)}`;
    }
    
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'boolean') {
      return (
        <span className={`px-2 py-1 rounded-pill text-small ${
          value ? 'bg-success-tint text-success' : 'bg-surface-sunken text-ink-muted'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    
    return value;
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="animate-pulse">
          <div className="h-4 bg-surface-sunken rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-surface-sunken rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Header with search and actions */}
      {(filterable || onExport || actions) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {filterable && (
              <div className="relative">
                <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-faint" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="min-h-touch pl-10 pr-4 rounded-control border border-border-strong bg-surface-card text-body text-ink placeholder:text-ink-muted transition-colors duration-state focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
                />
              </div>
            )}

            {selectedRows.size > 0 && (
              <span className="text-small text-ink-muted">
                {selectedRows.size} selected
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {onExport && (
              <button
                onClick={() => onExport(sortedData)}
                className="flex items-center px-3 py-2 text-small text-ink-soft border border-border-default hover:bg-surface-sunken rounded-control transition-colors duration-state"
              >
                <Download size={16} strokeWidth={1.5} className="mr-2" />
                Export
              </button>
            )}

            {actions && (
              <div className="flex items-center space-x-2">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        data-testid="datatable-table"
        className={`overflow-x-auto rounded-card border border-border-default bg-surface-card ${
          renderCard ? 'hidden sm:block' : ''
        }`}
      >
        <table className="min-w-full divide-y divide-border-default bg-surface-card">
          <thead className="bg-surface-sunken">
            <tr>
              {selectable && (
                <th className={`px-6 ${cellPadding} text-left border-b border-border-default`}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 accent-accent focus:ring-accent/40 border-border-strong rounded"
                  />
                </th>
              )}

              {columns.map((column) => (
                <th
                  key={column.accessor}
                  className={`px-6 ${cellPadding} text-label uppercase text-ink-muted border-b border-border-default ${
                    column.numeric ? 'text-right' : 'text-left'
                  } ${column.stickyRight ? STICKY_RIGHT_HEAD : ''} ${
                    sortable && column.sortable !== false ? 'cursor-pointer hover:bg-surface-sunken' : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column.accessor)}
                >
                  <div className={`flex items-center space-x-1 ${column.numeric ? 'justify-end' : ''}`}>
                    <span>{column.header}</span>
                    {sortable && column.sortable !== false && (
                      <div className="flex flex-col">
                        <ChevronUp
                          className={`w-3 h-3 ${
                            sortConfig.key === column.accessor && sortConfig.direction === 'asc'
                              ? 'text-accent'
                              : 'text-ink-muted'
                          }`}
                          strokeWidth={1.5}
                        />
                        <ChevronDown
                          className={`w-3 h-3 -mt-1 ${
                            sortConfig.key === column.accessor && sortConfig.direction === 'desc'
                              ? 'text-accent'
                              : 'text-ink-muted'
                          }`}
                          strokeWidth={1.5}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}

              {onRowClick && (
                <th className={`px-6 ${cellPadding} text-right text-label uppercase text-ink-muted border-b border-border-default`}>
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody className="bg-surface-card divide-y divide-border-default">
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (onRowClick ? 1 : 0)}
                  className="px-6 py-12 text-center text-ink-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={index}
                  className={`group border-b border-border-default hover:bg-surface-sunken transition-colors duration-state ${
                    onRowClick ? 'cursor-pointer' : ''
                  } ${
                    selectedRows.has(index) ? 'bg-accent-tint' : ''
                  }`}
                  onClick={() => onRowClick?.(item)}
                >
                  {selectable && (
                    <td className={`px-6 ${cellPadding}`}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(index)}
                        onChange={(e) => handleRowSelect(index, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 accent-accent focus:ring-accent/40 border-border-strong rounded"
                      />
                    </td>
                  )}

                  {columns.map((column) => (
                    <td
                      key={column.accessor}
                      className={`px-6 ${cellPadding} whitespace-nowrap text-body ${
                        column.className || (column.numeric ? 'font-mono tabular-nums text-right text-ink' : 'text-ink-soft')
                      } ${column.stickyRight ? STICKY_RIGHT_CELL : ''}`}
                    >
                      {renderCell(item, column)}
                    </td>
                  ))}

                  {onRowClick && (
                    <td className={`px-6 ${cellPadding} whitespace-nowrap text-right text-body font-medium`}>
                      <button
                        className="text-accent hover:text-accent-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(item);
                        }}
                      >
                        <Eye size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {renderCard && (
        <div data-testid="datatable-cards" className="sm:hidden space-y-2">
          {paginatedData.length === 0 ? (
            <div className="rounded-card border border-border-default bg-surface-card px-4 py-8 text-center text-ink-muted">
              {emptyMessage}
            </div>
          ) : (
            paginatedData.map((item, index) => (
              <React.Fragment key={index}>{renderCard(item, index)}</React.Fragment>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-small text-ink-muted">
            Showing <span className="font-mono tabular-nums">{((currentPage - 1) * pageSize) + 1}</span> to <span className="font-mono tabular-nums">{Math.min(currentPage * pageSize, sortedData.length)}</span> of <span className="font-mono tabular-nums">{sortedData.length}</span> results
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-small text-ink-soft border border-border-default hover:bg-surface-sunken rounded-control transition-colors duration-state disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} strokeWidth={1.5} className="mr-1" />
              Previous
            </button>

            <div className="flex items-center space-x-1">
              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-small font-mono tabular-nums border rounded-control transition-colors duration-state ${
                      currentPage === pageNum
                        ? 'bg-accent text-white border-accent'
                        : 'border-border-default text-ink-soft hover:bg-surface-sunken'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-small text-ink-soft border border-border-default hover:bg-surface-sunken rounded-control transition-colors duration-state disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} strokeWidth={1.5} className="ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Quick action buttons for table rows
export const TableActions = ({ onView, onEdit, onDelete, item }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="p-2 text-ink-muted hover:text-ink transition-colors duration-state"
      >
        <MoreVertical size={16} strokeWidth={1.5} />
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-surface-card rounded-card shadow-popover z-10 border border-border-default">
          <div className="py-1">
            {onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(item);
                  setShowMenu(false);
                }}
                className="flex items-center w-full px-4 py-2 text-body text-ink-soft hover:bg-surface-sunken transition-colors duration-state"
              >
                <Eye size={16} strokeWidth={1.5} className="mr-2" />
                View
              </button>
            )}

            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                  setShowMenu(false);
                }}
                className="flex items-center w-full px-4 py-2 text-body text-ink-soft hover:bg-surface-sunken transition-colors duration-state"
              >
                <Edit size={16} strokeWidth={1.5} className="mr-2" />
                Edit
              </button>
            )}

            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                  setShowMenu(false);
                }}
                className="flex items-center w-full px-4 py-2 text-body text-danger hover:bg-danger-tint transition-colors duration-state"
              >
                <Trash2 size={16} strokeWidth={1.5} className="mr-2" />
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};